import React, { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { BookOpen, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getDocsBySlug } from '../data/docsContent';

const ACCENTS = {
  teal: { text: 'text-teal-400', border: 'border-teal-400/40', bg: 'bg-teal-400/10', dot: 'bg-teal-400' },
  amber: { text: 'text-amber-400', border: 'border-amber-400/40', bg: 'bg-amber-400/10', dot: 'bg-amber-400' },
};

export default function DocsPage() {
  const { slug } = useParams();
  const [activeSection, setActiveSection] = useState(null);
  const doc = getDocsBySlug(slug);

  useEffect(() => {
    if (doc?.content?.sections?.length) setActiveSection(doc.content.sections[0].id);
  }, [doc]);

  if (!doc || doc.comingSoon) {
    return <Navigate to="/docs" replace />;
  }

  const { content } = doc;
  const accent = ACCENTS[content.accent] || ACCENTS.teal;

  return (
    <div className="min-h-screen bg-bots-dark text-white font-sans">
      <Header />

      <section className="pt-28 pb-6 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4" data-testid="docs-breadcrumb">
            <Link to="/docs" className="hover:text-white flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> Docs</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-gray-300">{content.name}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-2">{content.name}</h1>
          <p className={`text-sm ${accent.text}`}>{content.tagline}</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-12 grid lg:grid-cols-[240px_1fr] gap-10">
        {/* Sidebar */}
        <nav className="lg:sticky lg:top-28 lg:self-start space-y-1" data-testid="docs-sidebar">
          {content.sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                activeSection === s.id ? `${accent.bg} ${accent.text} font-semibold` : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`docs-nav-${s.id}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${activeSection === s.id ? accent.dot : 'bg-gray-600'}`} />
              {s.title}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="min-w-0" data-testid="docs-content">
          {content.sections.map((s) => (
            <div
              key={s.id}
              id={s.id}
              className={activeSection === s.id ? 'block' : 'hidden'}
              data-testid={`docs-section-${s.id}`}
            >
              <h2 className={`text-2xl font-bold mb-6 pb-3 border-b ${accent.border}`}>{s.title}</h2>
              <div className="space-y-4">
                {s.body.map((p, i) => (
                  <p key={i} className="text-gray-300 leading-relaxed text-[15px] whitespace-pre-line">{p}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
